import { TokenSelector } from "components/TokenSelector/TokenSelector";
import React, { useEffect, useRef, useState } from "react";
import { GlobalStore } from "../../../store/global/GlobalStore";
import { MAX_SELECTABLE_TOKENS } from "config/consts";
import { useTotalUSDAmount } from "hooks/useTotalUsdAmount";
import { TotalTradeAmount } from "./TotalTradeAmount";
import { AmountBar } from "components/AmountBar/AmountBar";
import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import { useOnClickOutside } from "usehooks-ts";
import { TradeLegTaker } from "components/TradeLeg/TradeLegTaker";
import { WarningOrError } from "../../../components/WarningOrError/WarningOrError";
import { getTradeErrorFromQuote, TRADE_SIZE_ERROR_CODE } from "../../../config/errorCodes";
import { Token } from "../../../store/global/TokenEnum";
import { SwitchTokensButton } from "components/ButterComponents/SwitchTokens/SwitchTokens";
import { Amount } from "types/amount";

export const TakerSection: React.FC = () => {
  const takerTokens = GlobalStore.useState(s => s.takerTokens);
  const makerTokens = GlobalStore.useState(s => s.makerTokens);
  const quoteTakerTokens = GlobalStore.useState(s => s.quote?.takerTokens);
  const quoteError = GlobalStore.useState(s => s.quoteError);
  const orderError = GlobalStore.useState(s => s.orderError);
  const chain = GlobalStore.useState(s => s.network);
  const totalUsdAmount = useTotalUSDAmount(takerTokens, false, quoteTakerTokens);
  const tokenList = useTokenListByChain();

  // Allows us to determine if the top or bottom leg on the taker side should be
  // focused. If the token switch button was just clicked, we want to focus on
  // the first leg. Or else, we focus on the last leg.
  const [isLastTradePageActionATokenSwitch, setIsLastTradePageActionATokenSwitch] = useState(false);

  const selectorRef = useRef<HTMLDivElement>(null);
  const [isWaitingToAddToken, setIsWaitingToAddToken] = useState(false);
  const [isChangingToken, setIsChangingToken] = useState<Token | undefined>(undefined);
  const showSelector = takerTokens.length === 0 || isChangingToken || isWaitingToAddToken;
  const errorMessageFromQuoteError = getTradeErrorFromQuote(chain, quoteError);

  useOnClickOutside(selectorRef, () => {
    setIsChangingToken(undefined);
    setIsWaitingToAddToken(false);
  });

  useEffect(() => {
    // Remove token selector leg when tokens are removed or added
    setIsWaitingToAddToken(false);
    setIsChangingToken(undefined);
    setIsLastTradePageActionATokenSwitch(false);
  }, [takerTokens]);

  return (
    <>
      {takerTokens.map((entry, index) => {
        const lastIndex = takerTokens.length - 1;
        const showPlusButton =
          index === lastIndex && makerTokens.length <= 1 && index !== MAX_SELECTABLE_TOKENS - 1;
        if (isChangingToken === entry.token) {
          return null;
        }
        return (
          <TradeLegTaker
            key={entry.token}
            symbol={entry.token}
            showPlusButton={showPlusButton}
            isWaitingToAddToken={isWaitingToAddToken}
            setIsWaitingToAddToken={setIsWaitingToAddToken}
            setIsChangingToken={setIsChangingToken}
            focusOnLoad={
              (isLastTradePageActionATokenSwitch && index === 0) ||
              (!isLastTradePageActionATokenSwitch && index === lastIndex)
            }
          />
        );
      })}

      {showSelector && (
        <TokenSelector ref={selectorRef} isChangingToken={isChangingToken} counterParty="taker" />
      )}
      {takerTokens.length > 1 && (
        <AmountBarContainer>
          <AmountBar counterParty={"taker"} totalUsdAmount={totalUsdAmount} />
          <TotalTradeAmount isVisible={tokenList !== undefined} totalUsdAmount={totalUsdAmount} />
        </AmountBarContainer>
      )}
      {errorMessageFromQuoteError &&
        quoteError?.error.errorCode === TRADE_SIZE_ERROR_CODE &&
        orderError === undefined && (
          <WarningOrError isTradeSizeError={true} {...errorMessageFromQuoteError} />
        )}
      <SwitchTokensButton
        isVisible={true}
        onClick={() => {
          switchTradeTokens();
          setIsLastTradePageActionATokenSwitch(true);
        }}
      />
    </>
  );
};

// Sub-components

const AmountBarContainer = styled.div`
  display: flex;
  padding: 12px 26px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

// Helpers

const switchTradeTokens = () => {
  GlobalStore.update(s => {
    const makerTokens = [...s.makerTokens];
    const takerTokens = [...s.takerTokens];
    s.makerTokens = takerTokens.map(token => {
      return {
        ...token,
        pct: 100 / takerTokens.length,
        isLocked: false,
        amount: Amount.ZERO,
      };
    });
    s.takerTokens = makerTokens;
    // Clear quote if there was one
    s.quote = undefined;
  });
};
